import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Check, X } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } },
};

const staggerContainer = { visible: { transition: { staggerChildren: 0.1 } } };
const slideInLeft = { hidden: { opacity: 0, x: -20 }, visible: { opacity: 1, x: 0, transition: { delay: 0.5, duration: 0.5 } } };
const slideInLeftSlow = { hidden: { opacity: 0, x: -20 }, visible: { opacity: 1, x: 0, transition: { delay: 0.7, duration: 0.5 } } };
const columnFadeIn = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { delay: 0.3, duration: 0.8 } } };
const columnFadeInSlow = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { delay: 0.5, duration: 0.8 } } };
const ctaFadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { delay: 1, duration: 0.8 } } };

const COMPARISONS = [
  { feature: "Private luxury vehicle & chauffeur", obsidian: true, standard: false },
  { feature: "Bilingual expert guide with deep local knowledge", obsidian: true, standard: false },
  { feature: "Curated gourmet dining experiences", obsidian: true, standard: false },
  { feature: "Flexible, personalized itineraries", obsidian: true, standard: false },
  { feature: "Small groups (max 6-8 guests)", obsidian: true, standard: false },
  { feature: "24/7 concierge & WhatsApp support", obsidian: true, standard: false },
  { feature: "Premium travel insurance included", obsidian: true, standard: false },
  { feature: "Access to hidden, off-trail locations", obsidian: true, standard: false },
  { feature: "Fixed schedule, large tour buses", obsidian: false, standard: true },
  { feature: "Generic multilingual guides", obsidian: false, standard: true },
  { feature: "Crowded tourist-only restaurants", obsidian: false, standard: true },
  { feature: "One-size-fits-all group itinerary", obsidian: false, standard: true },
];

function FeatureRow({ feature, icon: Icon, iconClass, textClass, variants }) {
  return (
    <motion.div key={feature} variants={variants} className="flex items-center gap-4 py-2">
      <div className={`w-7 h-7 border flex items-center justify-center shrink-0 ${iconClass}`}>
        <Icon size={14} className={textClass} strokeWidth={2} />
      </div>
      <span className={`font-sans text-sm ${textClass}`}>{feature}</span>
    </motion.div>
  );
}

export default function WhyObsidian() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const obsidianFeatures = COMPARISONS.filter(c => c.obsidian);
  const standardFeatures = COMPARISONS.filter(c => c.standard);
  const animate = inView ? "visible" : "hidden";

  return (
    <section id="why-obsidian" data-testid="why-obsidian" className="py-24 md:py-32 lg:py-40 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div ref={ref} initial="hidden" animate={animate} variants={staggerContainer} className="text-center mb-16 md:mb-20">
          <motion.p variants={fadeUp} className="font-sans text-[#C5A059] text-[10px] md:text-xs tracking-[0.25em] uppercase mb-4">The Difference</motion.p>
          <motion.h2 variants={fadeUp} className="font-serif text-white text-4xl md:text-5xl font-light tracking-tight mb-6">Why <span className="italic">Obsidian</span></motion.h2>
          <motion.p variants={fadeUp} className="font-sans text-white/60 text-sm md:text-base font-light max-w-2xl mx-auto leading-relaxed">
            We don't do tours. We craft experiences. Here's what sets an Obsidian journey apart from a standard tourist package.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <motion.div initial="hidden" animate={animate} variants={columnFadeIn} className="border border-[#C5A059]/30 bg-[#C5A059]/[0.03] p-8 md:p-10" data-testid="obsidian-column">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-10 h-10 border border-[#C5A059] flex items-center justify-center"><div className="w-3 h-3 bg-[#C5A059] rotate-45" /></div>
              <div>
                <h3 className="font-serif text-white text-2xl font-light">Obsidian</h3>
                <p className="font-sans text-[#C5A059] text-[10px] tracking-[0.2em] uppercase">Bespoke Experience</p>
              </div>
            </div>
            <motion.div initial="hidden" animate={animate} className="space-y-4">
              {obsidianFeatures.map(item => (
                <FeatureRow key={item.feature} feature={item.feature} icon={Check} iconClass="border-[#C5A059]/30" textClass="text-white/70" variants={slideInLeft} />
              ))}
            </motion.div>
          </motion.div>

          <motion.div initial="hidden" animate={animate} variants={columnFadeInSlow} className="border border-white/[0.06] p-8 md:p-10" data-testid="standard-column">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-10 h-10 border border-white/10 flex items-center justify-center"><div className="w-3 h-3 border border-white/20 rotate-45" /></div>
              <div>
                <h3 className="font-serif text-white/60 text-2xl font-light">Standard</h3>
                <p className="font-sans text-white/20 text-[10px] tracking-[0.2em] uppercase">Group Tour Package</p>
              </div>
            </div>
            <motion.div initial="hidden" animate={animate} className="space-y-4">
              {standardFeatures.map(item => (
                <FeatureRow key={item.feature} feature={item.feature} icon={X} iconClass="border-white/[0.06]" textClass="text-white/25 line-through decoration-white/10" variants={slideInLeftSlow} />
              ))}
            </motion.div>
          </motion.div>
        </div>

        <motion.div initial="hidden" animate={animate} variants={ctaFadeIn} className="text-center mt-16">
          <a href="#bespoke" data-testid="why-obsidian-cta"
            className="inline-block border border-[#C5A059] text-[#C5A059] font-sans text-xs tracking-[0.2em] uppercase px-10 py-4 hover:bg-[#C5A059] hover:text-black transition-all duration-500">
            Start Your Journey
          </a>
        </motion.div>
      </div>
    </section>
  );
}
